import { createAsyncThunk } from '@reduxjs/toolkit';
import { services } from '../../../api';
import { ChatType } from '../../../shared/utils/enums';

export interface Message {
  id: string;
  content: string;
  isUser: boolean;
  timestamp: Date;
  isStreaming?: boolean;
}

export interface ChatRequest {
  question: string;
  type: ChatType;
  patientId?: number;
  context?: any;
  thread_id?: number;
}

let abortController: AbortController | null = null;

export const fetchChatThreads = createAsyncThunk(
  'qa/fetchChatThreads',
  async (type: ChatType = ChatType.QNA, { rejectWithValue }) => {
    try {
      const response = await services.chat.getChatThreads(type);
      return response.content;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch chat threads');
    }
  }
);

export const fetchChatThreadDetails = createAsyncThunk(
  'qa/fetchChatThreadDetails',
  async (threadId: number, { rejectWithValue }) => {
    try {
      const response = await services.chat.getChatThreadDetails(threadId);
      return response.content;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch thread details');
    }
  }
);

export const submitStreaming = async (
  request: ChatRequest,
  onChunk: (chunk: string) => void,
  onComplete: () => void,
  onError: (error: string) => void
) => {
  try {
    // Cancel any existing request
    if (abortController) {
      abortController.abort();
    }

    // Create new abort controller
    abortController = new AbortController();

    await services.chat.submitChatStreaming(request, onChunk, onComplete, onError);
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      console.log('Request cancelled');
    } else {
      onError(error instanceof Error ? error.message : 'An error occurred');
    }
  } finally {
    abortController = null;
  }
};

export const abortStreaming = () => {
  if (abortController) {
    abortController.abort();
    abortController = null;
  }
};

export const sendMessage = createAsyncThunk(
  'qa/sendMessage',
  async ({
    question,
    threadId,
    onChunk,
    onComplete,
    onError,
  }: {
    question: string;
    threadId?: number;
    onChunk: (chunk: string, threadId?: string, title?: string) => void;
    onComplete: () => void;
    onError: (error: string) => void;
  }) => {
    try {
      const request: ChatRequest = {
        question,
        type: ChatType.QNA,
      };

      // Only add thread_id if it exists
      if (threadId) {
        request.thread_id = threadId;
      }

      await services.chat.submitChatStreaming(
        request,
        onChunk,
        onComplete,
        onError
      );
    } catch (error) {
      onError(error instanceof Error ? error.message : 'An error occurred');
      throw error;
    }
  }
);
